import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Rocket,
  Users,
  Car,
  Package,
  FileText,
  FileQuestion,
  CreditCard,
  Banknote,
  ShieldCheck,
  Ticket,
  MessageSquare,
  Bell,
  Star,
  UserCog,
  Sliders,
  ListChecks,
  HelpCircle,
  Search,
  Smartphone,
  Globe,
  type LucideIcon,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Modelo de contenido                                               */
/* ------------------------------------------------------------------ */

type Block =
  | { type: "p"; text: string }
  | { type: "steps"; items: string[] }
  | { type: "bullets"; items: string[] }
  | { type: "note"; text: string };

type DocTopic = { title: string; blocks: Block[] };
type DocSection = {
  id: string;
  title: string;
  icon: LucideIcon;
  description: string;
  topics: DocTopic[];
};

/* ------------------------------------------------------------------ */
/*  Contenido de la documentación                                     */
/* ------------------------------------------------------------------ */

const SECTIONS: DocSection[] = [
  {
    id: "primeros-pasos",
    title: "Primeros pasos",
    icon: Rocket,
    description: "Qué es el sistema y cómo moverse por él.",
    topics: [
      {
        title: "¿Qué es el sistema Oriental Ramírez?",
        blocks: [
          {
            type: "p",
            text: "Es una plataforma digital integral para la venta y financiamiento de vehículos, venta de repuestos, garantías, rifas y comunidad de clientes. Se compone de tres aplicaciones que comparten la misma base de datos.",
          },
          {
            type: "bullets",
            items: [
              "Sitio web (orientalramirez.com): la vitrina pública para clientes.",
              "App móvil: para clientes (Android e iOS) y también para el personal.",
              "Panel de administración (este dashboard): la herramienta principal del personal.",
            ],
          },
          {
            type: "note",
            text: "Todo está conectado: un cambio hecho en cualquiera de las tres aplicaciones se refleja en las demás en tiempo real.",
          },
        ],
      },
      {
        title: "Cómo navegar el dashboard",
        blocks: [
          {
            type: "p",
            text: "El dashboard se divide en dos áreas: la barra lateral izquierda con el menú, y el área principal donde se trabaja.",
          },
          {
            type: "bullets",
            items: [
              "General: el Panel con el resumen del negocio.",
              "Catálogo: Vehículos y Repuestos.",
              "Ventas: Cotizaciones, Solicitudes Web, Financiamiento, Pagos y Garantías.",
              "Comunidad: Usuarios, Rifas, Notificaciones y Sugerencias.",
              "Configuración: Ajustes y esta Documentación.",
            ],
          },
        ],
      },
      {
        title: "El Panel principal",
        blocks: [
          {
            type: "p",
            text: "Es la pantalla de inicio. Muestra un resumen del negocio en tiempo real para una visión rápida del estado de la operación.",
          },
          {
            type: "bullets",
            items: [
              "Indicadores clave: vehículos en inventario, cotizaciones, financiamientos pendientes y usuarios.",
              "Gráficos de financiamientos por estado y de cotizaciones por mes.",
              "Inventario separado entre motocicletas y automóviles.",
              "Actividad reciente y accesos rápidos para crear vehículos, cotizaciones o enviar notificaciones.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "roles",
    title: "Roles y permisos",
    icon: Users,
    description: "Quién puede hacer qué dentro del sistema.",
    topics: [
      {
        title: "Roles disponibles",
        blocks: [
          {
            type: "bullets",
            items: [
              "Administrador: acceso completo al panel de administración; gestiona todo el sistema.",
              "Vendedor: personal de ventas; gestiona clientes, cotizaciones y solicitudes.",
              "Cliente: usuario final; explora, compra, solicita financiamiento y participa en la comunidad.",
              "Mecánico: atiende consultas técnicas y tickets de soporte de clientes con garantía.",
              "Garantizador: registra y administra las garantías y sus servicios de mantenimiento.",
              "Suplidor: proveedor de productos.",
            ],
          },
          {
            type: "note",
            text: "El rol de cada persona se asigna y modifica desde Comunidad → Usuarios.",
          },
        ],
      },
      {
        title: "Niveles de cliente",
        blocks: [
          {
            type: "p",
            text: "Los clientes avanzan por niveles según su actividad: Bronce → Plata → Oro → Platino → Diamante. Los niveles superiores otorgan beneficios como descuentos progresivos y acceso a ofertas exclusivas.",
          },
        ],
      },
    ],
  },
  {
    id: "vehiculos",
    title: "Vehículos",
    icon: Car,
    description: "Subir, editar y eliminar vehículos del inventario.",
    topics: [
      {
        title: "Subir (crear) un vehículo",
        blocks: [
          {
            type: "steps",
            items: [
              "Entre a Catálogo → Vehículos y pulse 'Nuevo vehículo'.",
              "Elija el tipo: Motocicleta o Automóvil (esto define qué especificaciones se solicitan).",
              "Complete la Información general: nombre/modelo, año, kilometraje, condición, categoría, marca, moneda y descripción.",
              "Indique los Precios: precio de venta, precio inicial (entrada mínima), precio mínimo y precio sugerido (interno).",
              "Complete las Especificaciones técnicas según el tipo de vehículo.",
              "Suba las imágenes del vehículo (JPG, PNG o WebP); se comprimen automáticamente.",
              "Guarde. El vehículo aparece de inmediato en el sitio web y la app.",
            ],
          },
          {
            type: "note",
            text: "Mientras más completa esté la ficha y mejores las fotos, más confianza genera y mejor se posiciona en búsquedas y filtros.",
          },
        ],
      },
      {
        title: "Campos de un vehículo",
        blocks: [
          {
            type: "p",
            text: "Campos obligatorios: nombre/modelo, categoría y marca. Los demás son opcionales pero recomendados.",
          },
          {
            type: "p",
            text: "Especificaciones de motocicleta: cilindrada (CC), cilindros, tipo de motor, refrigeración, transmisión, velocidades, arranque, potencia, torque, velocidad máxima, tanque, consumo, suspensión, frenos, dimensiones, peso y características como ABS, panel digital, cargador USB, control de tracción, quickshifter y modos de conducción.",
          },
          {
            type: "p",
            text: "Especificaciones de automóvil: carrocería, puertas, asientos, tipo de motor, cilindrada, transmisión, potencia, torque, consumo, dimensiones, maletero, airbags, asistencias de seguridad, aire acondicionado, sunroof, asientos de cuero/eléctricos, pantalla táctil, puertos USB, sonido y conectividad (Bluetooth, Apple CarPlay, Android Auto).",
          },
        ],
      },
      {
        title: "Editar un vehículo",
        blocks: [
          {
            type: "steps",
            items: [
              "En Catálogo → Vehículos, ubique el vehículo (puede usar la búsqueda o los filtros).",
              "Pulse el botón de editar.",
              "Modifique los campos necesarios en el formulario.",
              "Guarde los cambios.",
            ],
          },
        ],
      },
      {
        title: "Eliminar un vehículo",
        blocks: [
          {
            type: "steps",
            items: [
              "Ubique el vehículo en la lista o abra su ficha de detalle.",
              "Pulse el botón de eliminar.",
              "Confirme la acción.",
            ],
          },
          {
            type: "note",
            text: "La eliminación es definitiva: el vehículo deja de mostrarse en el sitio web y la app. Verifique antes de confirmar.",
          },
        ],
      },
      {
        title: "Buscar y filtrar el inventario",
        blocks: [
          {
            type: "bullets",
            items: [
              "Búsqueda por nombre, modelo o marca.",
              "Filtros por tipo, categoría y marca.",
              "Ordenamiento por nombre, año, precio o fecha.",
              "Paginación ajustable (10, 20, 50 o 100 por página).",
            ],
          },
        ],
      },
      {
        title: "Categorías y marcas",
        blocks: [
          {
            type: "p",
            text: "Las categorías y marcas clasifican cada vehículo. Se gestionan (crean, editan, eliminan) desde el panel de administración de la app móvil; en el dashboard se consultan en las pestañas Categorías y Marcas.",
          },
        ],
      },
    ],
  },
  {
    id: "repuestos",
    title: "Repuestos",
    icon: Package,
    description: "Inventario de piezas y lubricantes.",
    topics: [
      {
        title: "Gestionar repuestos",
        blocks: [
          {
            type: "p",
            text: "En Catálogo → Repuestos se administra el inventario de piezas. La lista muestra imagen, nombre, SKU, categoría, marca, precio y stock, con avisos de 'agotado' o 'stock bajo'.",
          },
          {
            type: "steps",
            items: [
              "Para crear: pulse 'Nuevo repuesto' y complete nombre, SKU, descripción, categoría, marca, precio, stock, moneda e imágenes.",
              "Para editar: pulse editar sobre el repuesto y modifique sus datos.",
              "Para eliminar: pulse eliminar y confirme.",
            ],
          },
          {
            type: "note",
            text: "Campos obligatorios: nombre, categoría y precio.",
          },
        ],
      },
    ],
  },
  {
    id: "cotizaciones",
    title: "Cotizaciones",
    icon: FileText,
    description: "Crear y gestionar cotizaciones formales.",
    topics: [
      {
        title: "Crear una cotización",
        blocks: [
          {
            type: "steps",
            items: [
              "Entre a Ventas → Cotizaciones y pulse 'Nueva cotización'.",
              "Datos del cliente: tipo (persona o empresa), nombre, correo, teléfono y dirección; si es empresa, agregue nombre y RNC/cédula.",
              "Vehículos: busque y agregue uno o varios del inventario; ajuste cantidad y precio. El sistema calcula subtotal, ITBIS (18%) y total.",
              "Financiamiento (opcional): active la opción e indique inicial, plazo y tasa; se calcula la cuota mensual.",
              "Notas: agregue notas para el cliente, notas internas y la fecha de validez.",
              "Guarde como borrador, o guarde y envíe (esto genera el número de cotización).",
            ],
          },
        ],
      },
      {
        title: "Estados de una cotización",
        blocks: [
          {
            type: "p",
            text: "Borrador → Enviada → Vista → Aceptada / Rechazada / Expirada.",
          },
          {
            type: "p",
            text: "La lista muestra estadísticas útiles: total de cotizaciones, monto acumulado, tasa de conversión y cotizaciones pendientes.",
          },
        ],
      },
    ],
  },
  {
    id: "solicitudes",
    title: "Solicitudes Web",
    icon: FileQuestion,
    description: "Gestionar las solicitudes que llegan desde el sitio web.",
    topics: [
      {
        title: "Atender una solicitud web",
        blocks: [
          {
            type: "p",
            text: "En Ventas → Solicitudes Web se gestionan las solicitudes de información enviadas por clientes desde la página web. Cada una muestra los datos de contacto, el vehículo de interés y el mensaje del cliente.",
          },
          {
            type: "bullets",
            items: [
              "Ver el detalle completo de la solicitud.",
              "Marcar como revisada.",
              "Crear una cotización directamente (los datos se cargan automáticamente).",
              "Rechazar la solicitud.",
              "Agregar notas internas.",
            ],
          },
          {
            type: "note",
            text: "Atienda las solicitudes a tiempo y conviértalas en cotizaciones para no perder oportunidades de venta.",
          },
        ],
      },
    ],
  },
  {
    id: "financiamientos",
    title: "Financiamientos",
    icon: CreditCard,
    description: "Seguimiento de las solicitudes de crédito.",
    topics: [
      {
        title: "Gestionar financiamientos",
        blocks: [
          {
            type: "p",
            text: "En Ventas → Financiamiento se ven todas las solicitudes de crédito, con fecha, cliente, contacto, vehículo, monto, inicial, plazo, estado y origen (app móvil, dashboard o cotización).",
          },
          {
            type: "steps",
            items: [
              "Abra el detalle de la solicitud para ver toda su información.",
              "Actualice el estado conforme avanza el proceso de evaluación.",
            ],
          },
        ],
      },
      {
        title: "Estados de un financiamiento",
        blocks: [
          {
            type: "p",
            text: "Pendiente → En revisión → Pre-aprobado → Aprobado → Completado / Rechazado / Cancelado.",
          },
          {
            type: "note",
            text: "Mantenga los estados al día: así el equipo y el cliente siempre saben en qué punto está cada gestión.",
          },
        ],
      },
    ],
  },
  {
    id: "pagos",
    title: "Pagos",
    icon: Banknote,
    description: "Registro y verificación de pagos.",
    topics: [
      {
        title: "Verificar pagos",
        blocks: [
          {
            type: "p",
            text: "En Ventas → Pagos se registran y verifican los pagos recibidos. La lista muestra fecha, usuario, monto, método (CardNet, Azul, TPago, PayPal, transferencia, efectivo, etc.), estado y referencia.",
          },
          {
            type: "steps",
            items: [
              "Para los pagos que requieren verificación manual, abra el detalle del pago.",
              "Revise el comprobante adjunto.",
              "Pulse 'Verificar' si es correcto, o 'Rechazar' si no procede.",
            ],
          },
          {
            type: "note",
            text: "Revise con cuidado cada comprobante antes de verificar un pago manual.",
          },
        ],
      },
      {
        title: "Estados de un pago",
        blocks: [
          {
            type: "p",
            text: "Pendiente → Procesando → Completado / Fallido / Cancelado / Reembolsado. Los pagos manuales pueden quedar 'Por verificar' hasta su revisión.",
          },
        ],
      },
    ],
  },
  {
    id: "garantias",
    title: "Garantías",
    icon: ShieldCheck,
    description: "Registro y seguimiento de garantías y servicios.",
    topics: [
      {
        title: "Cómo funciona el sistema de garantías",
        blocks: [
          {
            type: "p",
            text: "El sistema permite registrar la garantía de cada vehículo vendido y dar seguimiento a su mantenimiento. En el dashboard, Ventas → Garantías muestra los usuarios con rol Garantizador y los plazos disponibles (1 mes, 3 meses, 6 meses, 1 año, 2 años, garantía extendida).",
          },
          {
            type: "p",
            text: "El registro y la administración detallada de cada garantía se realiza desde el panel de la app móvil, con el rol Garantizador.",
          },
        ],
      },
      {
        title: "Tareas del Garantizador",
        blocks: [
          {
            type: "bullets",
            items: [
              "Tablero con estadísticas: garantías activas, servicios pendientes y servicios del día.",
              "Crear una garantía: datos del vehículo (marca, modelo, chasis, placa), datos del cliente, duración, plantilla de calendario de servicios y términos; el cliente firma digitalmente.",
              "Calendario de servicios: cada garantía incluye los mantenimientos programados (por ejemplo, a los 1.000 km, 3.000 km, cambios de aceite, inspecciones).",
              "Registrar servicios: fecha, tipo, piezas usadas, costo, kilometraje y notas.",
              "Reportes de garantías y servicios completados.",
            ],
          },
        ],
      },
      {
        title: "La garantía vista por el cliente",
        blocks: [
          {
            type: "p",
            text: "El cliente ve sus garantías en la app, en Mi cuenta → Mis garantías: número de garantía, datos del vehículo, fechas de vigencia, estado (activa, vencida o cancelada) y el calendario de servicios. La app envía recordatorios automáticos cuando se acerca un servicio (por fecha o kilometraje).",
          },
        ],
      },
    ],
  },
  {
    id: "rifas",
    title: "Rifas",
    icon: Ticket,
    description: "Crear rifas, modos de participación y sorteos.",
    topics: [
      {
        title: "Crear una rifa",
        blocks: [
          {
            type: "p",
            text: "La creación y edición completa de rifas se realiza desde el panel de administración de la app móvil. Una rifa se define con:",
          },
          {
            type: "bullets",
            items: [
              "Premio: el vehículo u objeto a sortear, con imagen y descripción.",
              "Boletas / números: la cantidad total de números disponibles.",
              "Precio por número: la rifa puede ser gratuita o de pago.",
              "Fechas: período de la rifa y fecha del sorteo.",
              "Reglas y términos de participación.",
            ],
          },
        ],
      },
      {
        title: "Modos de participación",
        blocks: [
          {
            type: "p",
            text: "El cliente participa eligiendo uno o varios números de la rifa:",
          },
          {
            type: "bullets",
            items: [
              "Selección de número: el cliente escoge un número disponible en una cuadrícula visual, con un buscador rápido de números.",
              "Modo giro / ruleta: en las rifas que lo permiten, el número se asigna mediante un giro.",
            ],
          },
          {
            type: "p",
            text: "Si la rifa es de pago, el cliente completa el pago del número; si es gratuita, confirma su participación con su cédula.",
          },
        ],
      },
      {
        title: "Estados y sorteo",
        blocks: [
          {
            type: "p",
            text: "Estados de una rifa: Próxima → Activa → En sorteo → Finalizada / Cancelada.",
          },
          {
            type: "p",
            text: "Desde Comunidad → Rifas en el dashboard se puede ver el detalle, consultar participantes, cambiar el estado y realizar el sorteo. El sistema selecciona el número ganador y notifica a los participantes.",
          },
        ],
      },
    ],
  },
  {
    id: "comunidad",
    title: "Comunidad y ofertas",
    icon: MessageSquare,
    description: "Muro social, moderación y ofertas promocionales.",
    topics: [
      {
        title: "El muro social",
        blocks: [
          {
            type: "p",
            text: "Los clientes pueden crear publicaciones (experiencias de compra, reseñas, recomendaciones) con texto, imágenes o video, y etiquetar vehículos. Otros usuarios reaccionan, comentan y comparten.",
          },
        ],
      },
      {
        title: "Moderación",
        blocks: [
          {
            type: "p",
            text: "Todas las publicaciones y reseñas pasan por moderación. Desde el panel de administración de la app móvil, el personal puede aprobar, rechazar, responder o eliminar publicaciones y reseñas, y gestionar los tickets de atención asociados a una reseña.",
          },
          {
            type: "note",
            text: "Revise periódicamente el contenido pendiente para mantener una comunidad sana y profesional.",
          },
        ],
      },
      {
        title: "Ofertas: ruleta y raspa-y-gana",
        blocks: [
          {
            type: "p",
            text: "Desde la app móvil, el administrador puede crear ofertas de venta promocionales con nombre, descripción, precio y productos incluidos. Estas ofertas se activan en dos mecánicas de juego para el cliente:",
          },
          {
            type: "bullets",
            items: [
              "Ruleta: el cliente gira una rueda y obtiene una oferta.",
              "Raspa y gana: el cliente 'raspa' para descubrir una oferta.",
            ],
          },
          {
            type: "p",
            text: "Cada oferta puede tener una probabilidad de aparición y un límite de canjes.",
          },
        ],
      },
    ],
  },
  {
    id: "anuncios",
    title: "Anuncios",
    icon: Bell,
    description: "Mensajes y avisos para los usuarios de la app.",
    topics: [
      {
        title: "Crear un anuncio",
        blocks: [
          {
            type: "p",
            text: "En Comunidad → Notificaciones se crean los anuncios que verán los usuarios dentro de la app.",
          },
          {
            type: "steps",
            items: [
              "Pulse para crear un nuevo anuncio.",
              "Escriba el título y el mensaje.",
              "Elija el tipo: Popup (ventana emergente), Banner o Toast (aviso breve).",
              "Defina el disparador: al abrir la app, al visitar una página, con retraso o manual.",
              "Opcionalmente defina una acción (ir a un producto, categoría, oferta, URL o abrir el carrito), texto del botón, imagen y fechas.",
              "Active el anuncio con su interruptor.",
            ],
          },
          {
            type: "note",
            text: "Los anuncios existentes se pueden editar, activar/desactivar o eliminar en cualquier momento.",
          },
        ],
      },
    ],
  },
  {
    id: "sugerencias",
    title: "Sugerencias",
    icon: Star,
    description: "Opiniones y valoraciones de los clientes.",
    topics: [
      {
        title: "Revisar sugerencias",
        blocks: [
          {
            type: "p",
            text: "En Comunidad → Sugerencias se revisan las opiniones de los clientes. Cada una muestra la calificación (1 a 5 estrellas), el comentario y la fecha.",
          },
          {
            type: "bullets",
            items: [
              "Aprobar una sugerencia.",
              "Rechazar una sugerencia.",
              "Responder al cliente.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "usuarios",
    title: "Usuarios",
    icon: UserCog,
    description: "Administrar a las personas registradas.",
    topics: [
      {
        title: "Gestionar usuarios",
        blocks: [
          {
            type: "p",
            text: "En Comunidad → Usuarios se administra a las personas registradas. La lista muestra nombre, correo, teléfono y rol, con búsqueda y filtro por rol.",
          },
          {
            type: "steps",
            items: [
              "Para editar un usuario, ábralo desde la lista.",
              "Modifique nombre, apellido, teléfono y, sobre todo, el rol.",
              "Guarde. El correo no es editable.",
            ],
          },
          {
            type: "note",
            text: "Las cuentas nuevas se crean desde el panel de la app móvil o cuando el cliente se registra por su cuenta. El cambio de rol es la herramienta clave para dar acceso a personal nuevo.",
          },
        ],
      },
    ],
  },
  {
    id: "configuracion",
    title: "Configuración",
    icon: Sliders,
    description: "Parámetros generales del negocio.",
    topics: [
      {
        title: "Ajustes del negocio",
        blocks: [
          {
            type: "p",
            text: "En Configuración → Ajustes se definen los parámetros generales, organizados en pestañas. Cada pestaña tiene su propio botón Guardar.",
          },
          {
            type: "bullets",
            items: [
              "Tienda: nombre, descripción, teléfono, correo, dirección y ubicación.",
              "Horarios: horario de atención por día de la semana.",
              "Fiscal / Legal: RNC y razón social.",
              "Financiamiento: módulo activo, si requiere aprobación, monto máximo, puntuación de crédito mínima y tasa base.",
              "Garantías: módulo activo, texto por defecto y datos de contacto.",
              "Notificaciones: canales habilitados.",
              "Redes sociales: enlaces a Facebook, Instagram, WhatsApp, YouTube, etc.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "movil",
    title: "Administrar desde el móvil",
    icon: Smartphone,
    description: "Qué se puede gestionar desde la app móvil.",
    topics: [
      {
        title: "El panel de administración móvil",
        blocks: [
          {
            type: "p",
            text: "La app móvil incluye un panel de administración completo para gestionar el negocio desde el teléfono. Está disponible para los usuarios con rol Administrador.",
          },
          {
            type: "bullets",
            items: [
              "Vehículos: crear, editar y eliminar, con todas sus especificaciones, imágenes y videos.",
              "Categorías y marcas: crear, editar y eliminar.",
              "Cotizaciones y financiamientos: crear y gestionar.",
              "Rifas: crear, editar y realizar sorteos.",
              "Ofertas de venta: crear ofertas para ruleta y raspa-y-gana.",
              "Comunidad: moderar publicaciones y reseñas.",
              "Usuarios: crear, editar y eliminar.",
              "Configuración del negocio: tienda, horarios, financiamiento, legal, notificaciones y redes.",
            ],
          },
          {
            type: "note",
            text: "La app y el dashboard comparten los mismos datos: lo que se hace en uno aparece de inmediato en el otro.",
          },
        ],
      },
    ],
  },
  {
    id: "web",
    title: "El sitio web público",
    icon: Globe,
    description: "Qué pueden hacer los visitantes en orientalramirez.com.",
    topics: [
      {
        title: "Funciones del sitio web",
        blocks: [
          {
            type: "bullets",
            items: [
              "Inicio: buscador y accesos rápidos a motocicletas, automóviles y lubricantes.",
              "Catálogo: búsqueda y filtros por tipo, condición, marca, categoría y presupuesto; vistas y moneda configurables.",
              "Ficha de vehículo: galería, especificaciones, calculadora de financiamiento y solicitud por WhatsApp o con Google.",
              "Repuestos: catálogo con filtros y carrito de compras.",
              "Financiamiento, Garantía, Sucursales (con mapa) y Nosotros.",
              "Registro e inicio de sesión con correo o con Google.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "glosario",
    title: "Glosario de estados",
    icon: ListChecks,
    description: "Referencia rápida de todos los estados del sistema.",
    topics: [
      {
        title: "Estados de cotización",
        blocks: [
          {
            type: "bullets",
            items: [
              "Borrador: la cotización está en preparación, aún no se envió.",
              "Enviada: se entregó al cliente.",
              "Vista: el cliente la abrió.",
              "Aceptada / Rechazada / Expirada: resultado final de la cotización.",
            ],
          },
        ],
      },
      {
        title: "Estados de financiamiento",
        blocks: [
          {
            type: "bullets",
            items: [
              "Pendiente: solicitud recibida, sin revisar.",
              "En revisión: el equipo está evaluando la solicitud.",
              "Pre-aprobado: aprobación inicial sujeta a documentos.",
              "Aprobado: financiamiento concedido.",
              "Completado: proceso finalizado.",
              "Rechazado / Cancelado: la solicitud no procedió.",
            ],
          },
        ],
      },
      {
        title: "Estados de pago",
        blocks: [
          {
            type: "bullets",
            items: [
              "Pendiente / Procesando: el pago está en curso.",
              "Completado: pago confirmado.",
              "Por verificar: pago manual a la espera de revisión.",
              "Fallido / Cancelado / Reembolsado: el pago no se concretó o se devolvió.",
            ],
          },
        ],
      },
      {
        title: "Estados de rifa y solicitud web",
        blocks: [
          {
            type: "bullets",
            items: [
              "Rifa: Próxima → Activa → En sorteo → Finalizada / Cancelada.",
              "Solicitud web: Pendiente → Revisada → Cotizada / Rechazada.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "faq",
    title: "Preguntas frecuentes",
    icon: HelpCircle,
    description: "Dudas habituales sobre el uso del sistema.",
    topics: [
      {
        title: "Subí o edité un vehículo, ¿cuándo se ve en la web y la app?",
        blocks: [
          {
            type: "p",
            text: "De inmediato. Las tres aplicaciones comparten la misma base de datos, por lo que los cambios aparecen al instante, sin necesidad de publicar ni esperar.",
          },
        ],
      },
      {
        title: "¿Puedo recuperar un vehículo eliminado?",
        blocks: [
          {
            type: "p",
            text: "No. La eliminación es definitiva. Si solo desea ocultarlo temporalmente, considere editarlo en lugar de eliminarlo. Verifique siempre antes de confirmar una eliminación.",
          },
        ],
      },
      {
        title: "¿Cómo le doy acceso de administrador a un empleado nuevo?",
        blocks: [
          {
            type: "p",
            text: "Pídale que se registre en la app o el sitio web. Luego, en Comunidad → Usuarios, búsquelo y cambie su rol al que corresponda (Administrador, Vendedor, Mecánico o Garantizador).",
          },
        ],
      },
      {
        title: "¿Dónde gestiono las rifas y la comunidad?",
        blocks: [
          {
            type: "p",
            text: "La creación de rifas, la moderación del muro social y las ofertas promocionales se hacen desde el panel de administración de la app móvil. Desde el dashboard se supervisan las rifas y se realizan los sorteos.",
          },
        ],
      },
      {
        title: "¿Por qué es importante mantener los estados actualizados?",
        blocks: [
          {
            type: "p",
            text: "Porque los estados son la forma en que todo el equipo —y el propio cliente— sabe en qué punto está cada gestión. Un financiamiento o una cotización con el estado desactualizado genera confusión y puede costar una venta.",
          },
        ],
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Render de bloques                                                 */
/* ------------------------------------------------------------------ */

function BlockView({ block }: { block: Block }) {
  if (block.type === "p") {
    return <p className="text-sm leading-relaxed text-muted-foreground">{block.text}</p>;
  }
  if (block.type === "steps") {
    return (
      <ol className="ml-1 space-y-1.5">
        {block.items.map((item, i) => (
          <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-muted-foreground">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
              {i + 1}
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ol>
    );
  }
  if (block.type === "bullets") {
    return (
      <ul className="space-y-1.5">
        {block.items.map((item, i) => (
          <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-muted-foreground">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  // note
  return (
    <div className="rounded-lg border border-primary/25 bg-primary/5 px-3 py-2.5 text-sm leading-relaxed text-foreground">
      <span className="font-semibold text-primary">Consejo: </span>
      {block.text}
    </div>
  );
}

function TopicCard({ topic }: { topic: DocTopic }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <h3 className="mb-3 font-display text-base font-semibold">{topic.title}</h3>
      <div className="space-y-3">
        {topic.blocks.map((block, i) => (
          <BlockView key={i} block={block} />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Página                                                            */
/* ------------------------------------------------------------------ */

function topicText(topic: DocTopic): string {
  return (
    topic.title +
    " " +
    topic.blocks
      .map((b) => (b.type === "p" || b.type === "note" ? b.text : b.items.join(" ")))
      .join(" ")
  ).toLowerCase();
}

export function Documentacion() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (q.length < 2) return [];
    const results: { section: DocSection; topic: DocTopic }[] = [];
    for (const section of SECTIONS) {
      for (const topic of section.topics) {
        if (topicText(topic).includes(q)) results.push({ section, topic });
      }
    }
    return results;
  }, [q]);

  const activeSection = SECTIONS.find((s) => s.id === activeId) ?? SECTIONS[0];
  const searching = q.length >= 2;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Documentación</h1>
        <p className="text-muted-foreground">
          Guía completa para administrar el sistema Oriental Ramírez: web, app móvil y este panel.
        </p>
      </div>

      {/* Buscador */}
      <div className="relative max-w-xl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar en la documentación (ej. 'eliminar vehículo', 'rifa', 'pago')…"
          className="pl-9"
        />
      </div>

      {searching ? (
        /* ---- Resultados de búsqueda ---- */
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {searchResults.length === 0
              ? "Sin resultados. Pruebe con otra palabra."
              : `${searchResults.length} resultado(s) para “${query}”.`}
          </p>
          {searchResults.map(({ section, topic }, i) => (
            <div key={i} className="space-y-2">
              <Badge variant="secondary" className="gap-1.5">
                <section.icon className="h-3 w-3" />
                {section.title}
              </Badge>
              <TopicCard topic={topic} />
            </div>
          ))}
        </div>
      ) : (
        /* ---- Vista normal: navegación + contenido ---- */
        <div className="grid gap-6 lg:grid-cols-[250px_1fr]">
          {/* Navegación */}
          <nav className="lg:sticky lg:top-4 lg:self-start">
            <div className="flex gap-1 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
              {SECTIONS.map((section) => {
                const Icon = section.icon;
                const active = section.id === activeId;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveId(section.id)}
                    className={`flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
                      active
                        ? "bg-primary/15 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="whitespace-nowrap lg:whitespace-normal">{section.title}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Contenido de la sección activa */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2.5">
                  <activeSection.icon className="h-5 w-5 text-primary" />
                  {activeSection.title}
                </CardTitle>
                <p className="text-sm text-muted-foreground">{activeSection.description}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {activeSection.topics.map((topic, i) => (
                  <TopicCard key={i} topic={topic} />
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
